import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users, UserPlus, Trash2, KeyRound, Loader2, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/auth/users`).then((r) => { setUsers(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/users`, form);
      setForm({ email: "", name: "", password: "", role: "staff" });
      toast.success("User created"); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Could not create user"); } finally { setBusy(false); }
  };

  const changeRole = async (u, role) => {
    try { await axios.patch(`${API}/auth/users/${u.id}`, { role }); toast.success(`${u.name} is now ${role}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change role"); load(); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.name}:`);
    if (!pw) return;
    try { await axios.patch(`${API}/auth/users/${u.id}`, { password: pw }); toast.success("Password updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/auth/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="mx-auto max-w-3xl p-4">
      <h2 className="mb-4 flex items-center gap-2 font-display text-2xl tracking-wide text-slate-900"><Users size={22} className="text-brand-700" /> Users</h2>

      <form onSubmit={create} data-testid="user-create-form" className="mb-5 grid gap-2 rounded-2xl border border-sand-300 bg-white p-4 sm:grid-cols-2">
        <input data-testid="new-user-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="h-10 rounded-lg border border-sand-300 bg-sand-50 px-3 text-sm outline-none focus:border-brand" />
        <input data-testid="new-user-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="h-10 rounded-lg border border-sand-300 bg-sand-50 px-3 text-sm outline-none focus:border-brand" />
        <input data-testid="new-user-password" required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="h-10 rounded-lg border border-sand-300 bg-sand-50 px-3 text-sm outline-none focus:border-brand" />
        <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
          className="h-10 rounded-lg border border-sand-300 bg-white px-3 text-sm outline-none focus:border-brand">
          <option value="staff">Staff</option>
          <option value="admin">Admin</option>
        </select>
        <button data-testid="new-user-submit" type="submit" disabled={busy}
          className="flex h-10 items-center justify-center gap-2 rounded-full bg-brand text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60 sm:col-span-2">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <UserPlus size={16} />} Add user
        </button>
      </form>

      {loading ? <div className="flex justify-center py-16 text-slate-400"><Loader2 className="animate-spin" /></div> : (
        <div className="space-y-2" data-testid="users-list">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-sand-300 bg-white p-3">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 font-semibold text-slate-800">
                  {u.role === "admin" && <Shield size={13} className="text-brand-700" />}{u.name}
                  {u.id === me.id && <span className="rounded bg-sand-100 px-1.5 py-0.5 text-[10px] text-slate-500">you</span>}
                </p>
                <p className="truncate text-xs text-slate-500">{u.email}</p>
              </div>
              <select data-testid={`user-role-${u.id}`} value={u.role} onChange={(e) => changeRole(u, e.target.value)}
                className="h-9 rounded-lg border border-sand-300 bg-white px-2 text-sm outline-none focus:border-brand">
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
              </select>
              <button data-testid={`user-resetpw-${u.id}`} onClick={() => resetPw(u)} title="Reset password"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-sand-100"><KeyRound size={15} /></button>
              <button data-testid={`user-delete-${u.id}`} onClick={() => del(u)} disabled={u.id === me.id} title="Delete"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10 disabled:opacity-30"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
